import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import LayoutDefault from 'common/layout/layout-default'
import TitleToolbar from 'common/title-toolbar/title-toolbar'
import Navigation from 'common/navigation/navigation'
import { tr } from '../../utils/translations/translate'
import {
    TEXT_CONTRACT,
    TEXT_CREATE_CREDIT_NOTE,
    TEXT_CREATE_CREDIT_NOTE_FOR_LINES,
    TEXT_INVOICE_LINE_SELECTED,
    TEXT_EDIT_CONTRACT_PRODUCTS,
    TEXT_SAVE,
    TEXT_PRODUCTS_TO_UPDATE,
    TEXT_SYNC_ERP,
    TEXT_NOT_INVOICED_LINES,
    TEXT_CREATE_INVOICE,
    TEXT_SELECT_INVOICE_FOR_DELETE,
    TEXT_DELETE_SELECTED_INVOICE_LINES,
    TEXT_DELETE_INVOICE
} from '../../utils/translations/keys'
import { RouteComponentProps } from '@reach/router'
import { makeStyles, Theme, Button, Grid } from '@material-ui/core'
import { ContractMainInfo } from './contract-main-info'
import { ContractProducts } from './contract-products'
import { ContractInvoices } from './contract-invoices'
import { ContractAudit } from './contract-audit'
import Spinner from 'common/spinner'
import { AppState } from '../../redux/app/app-store'
import { actionLoadRefunds } from '../../redux/refund/refund-actions'
import ContractClient from 'clients/contract-client'
import { RefundDialog } from './refund-dialog'
import { getContractMatrikkelNumberLong, getContractOrganizationNumber, getMatrikkelNr } from 'utils/contract-utils'
import { CreateInvoiceDialog } from './create-invoice-dialog'
import { ContractProductsEdit } from './contract-products-edit'
import { ContractProductUpdateSet } from '../../models/contract-product-update-set'
import {
    actionClearSelectedInvoices,
    actionContractGet,
    actionDeleteInvoices,
    actionLoadContractInvoices,
    actionUpdateContractCustomTerms,
    actionUpdateContractSerialNumberSuccess,
    actionUpdateSelectedContractInvoices,
} from '../../redux/contract/contract-actions'
import { CreateNewInvoice } from './create-new-invoice'
import { DeleteInvoiceDialog } from './delete-dialog'
import { DeleteInvoiceLine } from 'models/delete-invoice'

const useStyles = makeStyles((theme: Theme) => {
    return {
        root: {
            flexGrow: 1,
        },
        auditGrid: {
            padding: theme.spacing(1),
            overflow: 'auto',
        },
        createInvoiceButton: {
            marginRight: '1rem',
            background: 'white',
        },
        button: {
            marginLeft: '1rem',
        },
    }
})

export const ContractDetailsPage: React.FC<
    RouteComponentProps<{
        contractId: string
        location: any
    }>
> = (props) => {
    const { contractId = '' } = props

    const [updateCount, setUpdateCount] = useState(0)
    const [isLoading, setIsLoading] = useState(false)
    const [isLoadingInvoices, setIsLoadingInvoices] = useState(false)
    const [refundDialogOpen, setRefundDialogOpen] = useState(false)
    const [createInvoiceOpen, setCreateInvoiceOpen] = useState(false)
    const [createNewInvoiceOpen, setCreateNewInvoiceOpen] = useState(false)
    const [isCreditNoteModeEnabled, setCreditNoteModeEnabled] = useState(false)
    const [productsEditMode, setProductsEditMode] = useState(false)
    const [productsUpdateSet, setProductsUpdateSet] = useState([] as ContractProductUpdateSet[])
    const [productsUpdateSetInvalid, setProductsUpdateSetInvalid] = useState(false)
    const forceUpdate = () => {
        return setUpdateCount((updateCount) => updateCount + 1)
    }

    const [isDeleteMode, setIsDeleteMode] = useState(false)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const isEditMode = productsEditMode || isCreditNoteModeEnabled || isDeleteMode
    const dispatch = useDispatch()
    const tenantConfig = useSelector((state: AppState) => state.config.tenantConfig)
    const contract = useSelector((state: AppState) => state.contracts.selectedContract)
    const invoiceLines = useSelector((state: AppState) => state.contracts.contractInvoiceLines)
    const selectedInvoiceLinesCount = useSelector(
        (state: AppState) => state.contracts.contractInvoiceLines.filter((x) => x.isSelected).length,
    )
    const unSentInvoiceLines = useSelector((state: AppState) => state.contracts.contractUnsentInvoiceLines)
    const isEditProductsAllowed = tenantConfig?.isEditProductsInContractAllowed || false
    const isNso = useSelector((state: AppState) => state.config.tenantConfig?.isNsoTenant)

    const getData = async () => {
        await loadInvoices()
    }

    const loadContract = () => {
        dispatch(actionContractGet(contractId))
    }

    const loadInvoices = async () => {
        dispatch(actionLoadContractInvoices(contractId))
    }

    const updateProductsUpdateSet = (productsToUpdate: ContractProductUpdateSet[]) => {
        setProductsUpdateSetInvalid(false)
        productsToUpdate.forEach((x) => {
            if (x.quantity < 0 || x.numerator < 0 || x.denominator < 1) {
                setProductsUpdateSetInvalid(true)
            }
        })
        setProductsUpdateSet(productsToUpdate)
        forceUpdate()
    }

    useEffect(() => {
        setIsLoading(true)
        loadContract()
        getData()
        setIsLoading(false)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleUseCustomTermChanged = (useCustomTerms: boolean) => {
        dispatch(
            actionUpdateContractCustomTerms(contract.id, useCustomTerms, contract.periodType, contract.selectedPeriods),
        )
    }

    const handleSelectedPeriodTypeChanged = (periodType: string) => {
        dispatch(
            actionUpdateContractCustomTerms(contract.id, contract.useCustomTerms, periodType, contract.selectedPeriods),
        )
    }

    const handleSelectedPeriodsChanged = (selectedPeriods: number[]) => {
        dispatch(
            actionUpdateContractCustomTerms(contract.id, contract.useCustomTerms, contract.periodType, selectedPeriods),
        )
    }

    const handleSerialNumberChanged = (serialNumber: string) => {
        dispatch(actionUpdateContractSerialNumberSuccess(serialNumber))
    }

    const handleCreateInvoiceClose = () => {
        clearSelectedInvoiceLines()
        setCreateInvoiceOpen(false)
    }

    const handleCreateNewInvoiceClose = () => {
        setCreateNewInvoiceOpen(false)
    }

    const clearSelectedInvoiceLines = () => {
        dispatch(actionClearSelectedInvoices())
    }

    const handleCreateInvoiceOk = async () => {
        setCreateInvoiceOpen(false)
        setCreateNewInvoiceOpen(false)
        await loadInvoices()
    }

    const handleCreateRefund = () => {
        setRefundDialogOpen(true)
    }

    const saveProducts = async () => {
        setIsLoading(true)
        await ContractClient.updateProducts(contractId, productsUpdateSet).toPromise()
        setProductsEditMode(false)
        setProductsUpdateSet([] as ContractProductUpdateSet[])
        loadContract()
        setIsLoading(false)
        await loadInvoices()
    }

    const syncContractWithERP = async () => {
        setIsLoading(true)
        const item = await ContractClient.syncContractWithErp(contractId).toPromise()
        loadContract()
        setIsLoading(false)
        await loadInvoices()
    }

    const handleRefundClose = () => {
        clearSelectedInvoiceLines()
        setRefundDialogOpen(false)
        setCreditNoteModeEnabled(false)
    }

    const handleRefundOk = (createNewInvoice: boolean) => {
        setRefundDialogOpen(false)
        dispatch(actionLoadRefunds())
        if (createNewInvoice) {
            setCreateInvoiceOpen(true)
        }

        loadInvoices()
        setCreditNoteModeEnabled(false)
    }

    const handleRefundMode = () => {
        setCreditNoteModeEnabled(true)
    }

    const handleDeleteMode = () => {
        setIsDeleteMode(true)
    }

    const handleDeleteSelectedInvoice = () => {
        setDeleteDialogOpen(true)
    }

    const handleDeleteOk = () => {
        setDeleteDialogOpen(false)
        setIsDeleteMode(false)

        const invoiceLinesToDelete: DeleteInvoiceLine[] = unSentInvoiceLines
            .filter((x) => x.isSelected)
            .map((x) => {
                return { invoiceId: x.id, lineNumber: x.lineNo }
            })

        dispatch(
            actionDeleteInvoices({
                contractId: contractId,
                invoiceLines: invoiceLinesToDelete,
            }),
        )

        clearSelectedInvoiceLines()
    }

    const handleDeleteClose = () => {
        clearSelectedInvoiceLines()
        setDeleteDialogOpen(false)
        setIsDeleteMode(false)
    }

    const beginProductsEditMode = () => {
        setProductsEditMode(true)
    }

    const endProductsEditMode = () => {
        setProductsEditMode(false)
        setProductsUpdateSet([] as ContractProductUpdateSet[])
    }

    const styles = useStyles()

    const renderEditProductButtons = () => {
        return (
            <>
                {!productsEditMode && (
                    <>
                        <Button
                            variant="outlined"
                            color="inherit"
                            className={styles.button}
                            onClick={beginProductsEditMode}
                        >
                            {tr(TEXT_EDIT_CONTRACT_PRODUCTS)}
                        </Button>
                    </>
                )}

                {productsEditMode && (
                    <>
                        <Button
                            variant="outlined"
                            color="inherit"
                            className={styles.button}
                            onClick={saveProducts}
                            disabled={productsUpdateSet.length === 0 || productsUpdateSetInvalid}
                        >
                            {tr(TEXT_SAVE)}
                        </Button>
                    </>
                )}
            </>
        )
    }

    const renderDeleteButtons = () => {
        return isDeleteMode ? (
            <Button
                variant="outlined"
                color="inherit"
                className={styles.button}
                onClick={handleDeleteSelectedInvoice}
                disabled={selectedInvoiceLinesCount === 0}
            >
                {tr(TEXT_DELETE_SELECTED_INVOICE_LINES)}
            </Button>
        ) : (
            <Button variant="outlined" color="inherit" className={styles.button} onClick={handleDeleteMode}>
                {tr(TEXT_DELETE_INVOICE)}
            </Button>
        )
    }

    const renderRefundButtons = () => {
        return isCreditNoteModeEnabled ? (
            <Button
                variant="outlined"
                color="inherit"
                className={styles.button}
                onClick={handleCreateRefund}
                disabled={selectedInvoiceLinesCount === 0}
            >
                {tr(TEXT_CREATE_CREDIT_NOTE_FOR_LINES)}
            </Button>
        ) : (
            <Button variant="outlined" color="inherit" className={styles.button} onClick={handleRefundMode}>
                {tr(TEXT_CREATE_CREDIT_NOTE)}
            </Button>
        )
    }

    const renderSyncWithErpButton = () => {
        return (
            <>
                <Button
                    variant="outlined"
                    color="inherit"
                    className={styles.button}
                    onClick={syncContractWithERP}
                    disabled={unSentInvoiceLines?.length === 0}
                >
                    {tr(TEXT_SYNC_ERP)}
                </Button>
            </>
        )
    }

    const toolbar = (
        <>
            {!isEditMode && (
                <TitleToolbar
                    title={isNso ? getContractOrganizationNumber(contract) : getContractMatrikkelNumberLong(contract)}
                    enableGoBack={true}
                >
                    {tenantConfig?.isContractBasedInvoicing && renderSyncWithErpButton()}
                    {isEditProductsAllowed && renderEditProductButtons()}
                    <Button
                        variant="outlined"
                        color="inherit"
                        className={styles.button}
                        onClick={() => setCreateNewInvoiceOpen(true)}
                        disabled={createNewInvoiceOpen || productsEditMode || isCreditNoteModeEnabled || isDeleteMode}
                    >
                        {tr(TEXT_CREATE_INVOICE)}
                    </Button>
                    {renderRefundButtons()}
                    {tenantConfig?.invoiceDeleteEnabled && renderDeleteButtons()}
                </TitleToolbar>
            )}
            {isCreditNoteModeEnabled && (
                <TitleToolbar
                    title={`${selectedInvoiceLinesCount} ${tr(TEXT_INVOICE_LINE_SELECTED)}`}
                    enableClose={true}
                    enableGoBack={false}
                    onClose={handleRefundClose}
                >
                    {renderRefundButtons()}
                </TitleToolbar>
            )}

            {isDeleteMode && (
                <TitleToolbar
                    title={
                        selectedInvoiceLinesCount > 0
                            ? `${selectedInvoiceLinesCount} ${tr(TEXT_INVOICE_LINE_SELECTED)}`
                            : tr(TEXT_SELECT_INVOICE_FOR_DELETE)
                    }
                    enableClose={true}
                    enableGoBack={false}
                    onClose={handleDeleteClose}
                >
                    {renderDeleteButtons()}
                </TitleToolbar>
            )}

            {productsEditMode && (
                <TitleToolbar
                    title={`${productsUpdateSet.length} ${tr(TEXT_PRODUCTS_TO_UPDATE)}`}
                    enableClose={true}
                    enableGoBack={false}
                    onClose={endProductsEditMode}
                >
                    {renderEditProductButtons()}
                </TitleToolbar>
            )}
        </>
    )

    const sentInvoices = invoiceLines.filter((x) => !!x.transferDate)

    return (
        <LayoutDefault
            navigation={<Navigation />}
            toolbar={toolbar}
            headerBackgroundColor={isEditMode ? '#757575' : ''}
        >
            {isLoading ? (
                <Spinner />
            ) : (
                <>
                    {contract && (
                        <Grid container className={styles.root} spacing={2}>
                            <Grid item xs={9}>
                                <ContractMainInfo
                                    handleUseCustomTermChanged={handleUseCustomTermChanged}
                                    handleSelectedPeriodTypeChanged={handleSelectedPeriodTypeChanged}
                                    handleSelectedPeriodsChanged={handleSelectedPeriodsChanged}
                                />
                                {contract.residentialInfo && (
                                    <>
                                        {!productsEditMode && (
                                            <ContractProducts
                                                residentialInfo={contract.residentialInfo}
                                                isProductBasedContract={contract.isProductBasedContract}
                                                isNso={tenantConfig?.isNsoTenant ?? false}
                                            />
                                        )}
                                        {productsEditMode && (
                                            <ContractProductsEdit
                                                residentialInfo={contract.residentialInfo}
                                                isProductBasedContract={contract.isProductBasedContract}
                                                onProductsUpdated={updateProductsUpdateSet}
                                            />
                                        )}
                                    </>
                                )}
                                {unSentInvoiceLines?.length > 0 && (
                                    <ContractInvoices
                                        title={tr(TEXT_NOT_INVOICED_LINES)}
                                        invoiceLines={unSentInvoiceLines}
                                        isLoadingInvoices={isLoadingInvoices}
                                        deleteMode={isDeleteMode}
                                    />
                                )}
                                {sentInvoices?.length > 0 && (
                                    <ContractInvoices
                                        title={tr('TEXT_HOUSINGUNIT_INVOICE_LINES')}
                                        invoiceLines={sentInvoices}
                                        isLoadingInvoices={isLoadingInvoices}
                                        refundMode={isCreditNoteModeEnabled}
                                        deleteMode={isDeleteMode}
                                    />
                                )}
                            </Grid>
                            <Grid className={styles.auditGrid}>
                                <ContractAudit contractId={contractId} />
                            </Grid>
                        </Grid>
                    )}
                </>
            )}
            {!!contract && (
                <>
                    <RefundDialog
                        contract={contract}
                        invoiceLines={invoiceLines.filter((x) => x.isSelected)}
                        open={refundDialogOpen}
                        handleClose={handleRefundClose}
                        handleOk={handleRefundOk}
                    ></RefundDialog>
                    <CreateInvoiceDialog
                        contract={contract}
                        open={createInvoiceOpen}
                        handleClose={handleCreateInvoiceClose}
                        handleOk={handleCreateInvoiceOk}
                    ></CreateInvoiceDialog>
                    <CreateNewInvoice
                        contract={contract}
                        open={createNewInvoiceOpen}
                        handleClose={handleCreateNewInvoiceClose}
                        handleOk={handleCreateInvoiceOk}
                    ></CreateNewInvoice>
                    <DeleteInvoiceDialog
                        open={deleteDialogOpen}
                        handleClose={handleDeleteClose}
                        handleOk={handleDeleteOk}
                    ></DeleteInvoiceDialog>
                </>
            )}
        </LayoutDefault>
    )
}
